import React from 'react';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { companyLogos } from '../data/mock';

const Hero = () => {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 grid-pattern" />
      <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[120px] animate-pulse-glow" />
      <div className="absolute bottom-1/4 -right-32 w-[400px] h-[400px] bg-pink-600/15 rounded-full blur-[100px] animate-pulse-glow delay-200" />
      
      {/* Vertical Line Accent */}
      <div className="absolute left-12 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-purple-500/30 to-transparent hidden lg:block" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Column - Text */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 mb-8 animate-slide-up opacity-0">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-sm text-white/60">Systems Architecture & AI Engineering</span>
            </div>

            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-white mb-6 animate-slide-up opacity-0 delay-100">
              AI Systems.
              <br />
              <span className="text-gradient-animated">Engineered.</span>
            </h1>

            <p className="text-lg text-white/50 max-w-lg mb-10 leading-relaxed animate-slide-up opacity-0 delay-200">
              At Transformative AI, we design and deploy AI-powered operating systems for real businesses—revenue, compliance, decisioning, and human capital.
            </p>

            <div className="flex flex-wrap gap-4 mb-16 animate-slide-up opacity-0 delay-300">
              <button
                onClick={() => scrollTo('#contact')}
                className="group flex items-center gap-3 px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-all"
              >
                Request a Demo
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollTo('#systems')}
                className="px-8 py-4 border border-white/20 text-white font-medium hover:bg-white/5 hover:border-white/30 transition-all"
              >
                View Our Systems
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 animate-slide-up opacity-0 delay-400">
              {[
                { label: 'Revenue Recovered', value: '$12M+' },
                { label: 'Systems Deployed', value: '50+' },
                { label: 'Client Retention', value: '98%' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-2xl lg:text-3xl font-display text-white mb-1">{stat.value}</div>
                  <div className="text-xs text-white/40 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Logo Visual */}
          <div className="relative hidden lg:flex items-center justify-center">
            <div className="relative">
              {/* Glow behind logo */}
              <div className="absolute inset-0 bg-purple-500/20 blur-[80px] rounded-full scale-150" />
              
              {/* Logo */}
              <img
                src={companyLogos.mainDark}
                alt="Transformative AI Systems"
                className="relative w-[400px] h-auto animate-float"
              />
              
              {/* Orbiting elements */}
              <div className="absolute -top-8 -right-8 w-24 h-24 border border-purple-500/30 rounded-full animate-pulse-glow" />
              <div className="absolute -bottom-4 -left-4 w-16 h-16 border border-pink-500/30 rounded-full animate-pulse-glow delay-300" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={() => scrollTo('#what-we-do')}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30 hover:text-white/60 transition-colors"
      >
        <ArrowDown className="w-5 h-5 animate-bounce" />
      </button>
    </section>
  );
};

export default Hero;
